import {computed, makeObservable, observable, action} from "mobx";
import ApiResponseHandler from "@eryxcoop/appyx-comm/src/errors/ApiResponseHandler";
import BudgetForm from "../../domain/BudgetForm";
import {default as handleErrorToast} from "../../components/toast/ErrorToast";
import {default as handleSuccessToast} from "../../components/toast/SuccessToast";

export default class BudgetFeature {

    constructor(application) {
        this._application = application;
        this._loaded = false;
        this._budgetForm = null;
        this._loadedYear = null;

        makeObservable(this, {
            _loaded: observable,
            _budgetForm: observable,
            loaded: computed,
            form: computed,
            importInflationCategoryGroups: action,
        });
    }

    async load() {
        this._createBudgetForm({});
        this._budgetForm.getFieldByName('year').clear();
        this._loaded = true;
    }

    clearYearSelection() {
        this._budgetForm.getFieldByName('year').clear();
        this._budgetForm.getFieldByName('salesBudgetFile').clear();
        this._budgetForm.getFieldByName('inflationCategoryGroupsFile').clear();
        this._loadedYear = null;
    }

    _selectedYear() {
        const value = this._budgetForm.getFieldValue('year');
        return value && typeof value === 'object' ? value.value : value;
    }

    get loaded() {
        return this._loaded;
    }

    get form() {
        return this._budgetForm;
    }

    yearOptions() {
        const currentYear = new Date().getFullYear();
        const offsets = [0];
        return offsets.map(offset => {
            const year = String(currentYear + offset);
            return {label: year, value: year};
        });
    }

    async formChanged(budget) {
        await this.loadBudgetForSelectedYear(budget);
    }

    async loadBudgetForSelectedYear() {
        const budget = this._budgetForm.formEntity();
        const year = budget.year && typeof budget.year === 'object' ? budget.year.value : budget.year;
        if (year && year !== this._loadedYear) {
            await this._loadForecastFilesForYear(year);
        }
    }

    _createBudgetForm(budget) {
        let salesBudgetFile = budget.salesForecastFile;
        let inflationForecastFile = budget.inflationForecastFile;
        if (this._budgetForm) {
            salesBudgetFile = this._budgetForm.formEntity().salesBudgetFile || salesBudgetFile;
            inflationForecastFile = this._budgetForm.formEntity().inflationForecastFile || inflationForecastFile;
        }
        this._budgetForm = BudgetForm.newFor(this, {
            year: budget.year,
            salesBudgetFile: salesBudgetFile,
            inflationCategoryGroupsFile: inflationForecastFile,
        });
        const yearField = this._budgetForm.getFieldByName('year');
        if (yearField.value) {
            yearField.updateValue(yearField.value.value);
        }
    }

    async _loadForecastFilesForYear(year) {
        const responseHandler = new ApiResponseHandler({
            handlesSuccess: (response) => {
                const budget = response.budget();
                this._loadedYear = String(budget.year);
                this._createBudgetForm(budget);
            },
            handlesError: () => {
                handleErrorToast("No se pudieron cargar los archivos del presupuesto.");
            },
        });
        return this._application.apiClient().getBudgetForYear(year, responseHandler);
    }

    async importSalesBudgetFile() {
        const file = this._budgetForm.getFieldValue('salesBudgetFile');
        const year = this._selectedYear();
        if (!(file instanceof File)) {
            return;
        }
        const responseHandler = new ApiResponseHandler({
            handlesSuccess: async () => {
                handleSuccessToast("Presupuesto de ventas importado exitosamente.");
            },
            handlesError: () => {
                handleErrorToast("No se pudo importar el presupuesto de ventas. Intente nuevamente.");
            },
        });
        return this._application.apiClient().importSalesBudget(year, file, responseHandler);
    }

    async importInflationCategoryGroups() {
        const file = this._budgetForm.getFieldValue('inflationCategoryGroupsFile');
        const year = this._selectedYear();
        if (!(file instanceof File)) {
            return;
        }
        const responseHandler = new ApiResponseHandler({
            handlesSuccess: async () => {
                handleSuccessToast("Inflación por categorías importada exitosamente.");
            },
            handlesError: () => {
                handleErrorToast("No se pudo importar la inflación por categorías. Intente nuevamente.");
            },
        });
        return this._application.apiClient().importInflationForecast(year, file, responseHandler);
    }
}
